import React from "react";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocCodeBlock,
  DocNavFooter,
  DocLink,
} from "@/components/docs";
import { docsColors, docsComponents } from "@/lib/docs-theme";

export default function HttpMonitoringPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Monitor Types", href: "/docs/monitors" },
    { label: "HTTP/HTTPS Monitoring" },
  ];

  return (
    <DocLayout
      title="HTTP/HTTPS Monitoring - Uptime Monitor Documentation"
      description="Learn how to monitor websites, APIs, and web services with HTTP/HTTPS monitoring. Configure advanced options like headers, authentication, and response validation."
      canonical="https://uptime-monitor.io/docs/monitors/http"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="HTTP/HTTPS Monitoring"
        description="Monitor websites, APIs, and web services with comprehensive HTTP/HTTPS checks including custom requests, response validation, and SSL certificate monitoring."
      />

      {/* Basic Configuration */}
      <DocSection title="Basic Configuration">
        <DocCard>
          <div className="space-y-4">
            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>URL</h3>
              <p className={`${docsColors.text.body} mb-2`}>
                The full URL to monitor, including protocol (http:// or https://).
              </p>
              <DocCodeBlock className="leading-relaxed">
                Examples:
                <br />
                https://example.com
                <br />
                https://api.example.com/health
                <br />
                http://internal-service:8080/status
              </DocCodeBlock>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Check Frequency</h3>
              <p className={docsColors.text.body}>
                How often to check your endpoint. Options range from 10 seconds to 60 minutes. More
                frequent checks provide faster issue detection but consume more resources.
              </p>
            </div>

            <div>
              <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Timeout</h3>
              <p className={docsColors.text.body}>
                Maximum time to wait for a response before considering the check failed. Default is
                10 seconds. Adjust based on your endpoint&apos;s expected response time.
              </p>
            </div>
          </div>
        </DocCard>
      </DocSection>

      {/* Request Configuration */}
      <DocSection title="Request Configuration">
        <div className="space-y-6">
          {/* Important Note */}
          <DocAlert variant="note">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className="text-amber-400">Note:</strong> Response bodies are limited to 8KB
              to prevent excessive data transfer. Only the first 8KB of the response will be
              downloaded for validation checks.
            </p>
          </DocAlert>

          {/* HTTP Methods */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>HTTP Methods</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Choose the appropriate HTTP method for your endpoint. Default is HEAD for efficiency.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>GET</span>
                <p className={docsColors.text.muted}>Retrieve full response</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>POST</span>
                <p className={docsColors.text.muted}>Submit data</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>HEAD</span>
                <p className={docsColors.text.muted}>Headers only (default)</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>PUT</span>
                <p className={docsColors.text.muted}>Update resource</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>DELETE</span>
                <p className={docsColors.text.muted}>Remove resource</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>PATCH</span>
                <p className={docsColors.text.muted}>Partial update</p>
              </div>
              <div>
                <span className={`font-medium ${docsColors.text.heading}`}>OPTIONS</span>
                <p className={docsColors.text.muted}>Check allowed methods</p>
              </div>
            </div>
          </DocCard>

          {/* Custom Headers */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Custom Headers</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Add custom HTTP headers to your requests. Useful for authentication, API keys, or
              custom requirements.
            </p>
            <DocCodeBlock>
              <div className="mb-2">Authorization: Bearer your-api-token</div>
              <div className="mb-2">X-API-Key: your-api-key</div>
              <div className="mb-2">Content-Type: application/json</div>
              <div>Accept: application/json</div>
            </DocCodeBlock>
            <p className={`text-sm ${docsColors.text.muted} mt-3`}>
              Enter each header on a new line in the format: Header-Name: value
            </p>
          </DocCard>

          {/* Request Body */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Request Body</h3>
            <p className={`${docsColors.text.body} mb-3`}>
              For POST, PUT, and PATCH requests, you can include a request body.
            </p>
            <p className={`text-sm ${docsColors.text.muted} mb-3`}>
              <strong>Important:</strong> Remember to set the appropriate Content-Type header (e.g.,
              application/json) when sending a request body.
            </p>
            <DocCodeBlock>
              {`{
  "action": "health_check"
}`}
            </DocCodeBlock>
          </DocCard>
        </div>
      </DocSection>

      {/* Response Validation */}
      <DocSection title="Response Validation">
        <div className="space-y-6">
          {/* Status Codes */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Expected Status Codes
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Define which HTTP status codes indicate a successful response. By default, any 2xx
              status code (200-299) is considered successful.
            </p>
            <DocCodeBlock className="mb-3 leading-relaxed">
              Examples:
              <br />
              200 (single status)
              <br />
              200,201,204 (multiple statuses)
              <br />
              200-299 (range)
              <br />
              200-299,301,302 (combination)
            </DocCodeBlock>
            <p className={`text-sm ${docsColors.text.muted}`}>
              Leave empty to accept all 2xx status codes (200-299) as successful.
            </p>
          </DocCard>

          {/* Response Headers */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Expected Response Headers
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Verify that specific headers are present in the response. The check fails if any
              expected header is missing or has the wrong value.
            </p>
            <DocCodeBlock className="leading-relaxed">
              X-Service-Status: healthy
              <br />
              X-API-Version: 2.0
              <br />
              Content-Type: *
            </DocCodeBlock>
            <p className={`text-sm ${docsColors.text.muted} mt-3`}>
              Use <code className={`${docsColors.bg.code} px-1`}>*</code> as the value to check that
              a header exists without validating its content.
            </p>
          </DocCard>

          {/* Response Body */}
          <DocCard>
            <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
              Expected Response Body
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Check if the response body contains specific text. Useful for verifying API responses
              or ensuring pages load correctly.
            </p>
            <DocCodeBlock className="leading-relaxed">
              Examples:
              <br />
              &quot;status&quot;:&quot;ok&quot;
              <br />
              &lt;h1&gt;Welcome&lt;/h1&gt;
              <br />
              &quot;healthy&quot;:true
            </DocCodeBlock>
            <p className={`text-sm ${docsColors.text.muted} mt-3`}>
              The check passes if the response body contains this exact substring (case-sensitive).
              Only the first 8KB of the response body is checked.
            </p>
          </DocCard>
        </div>
      </DocSection>

      {/* SSL Certificate Options */}
      <DocSection title="SSL Certificate Monitoring">
        <DocCard>
          <div className="mb-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
              Check Certificate Expiration
            </h3>
            <p className={`${docsColors.text.body} mb-3`}>
              Enable this option to monitor SSL certificate expiration for HTTPS URLs. You&apos;ll
              receive warnings at:
            </p>
            <ul className={`space-y-1 ${docsColors.text.body} ml-6`}>
              <li className="flex items-center">
                <span className="text-yellow-400 mr-2">⚠️</span>
                30 days before expiration
              </li>
              <li className="flex items-center">
                <span className="text-orange-400 mr-2">⚠️</span>
                14 days before expiration
              </li>
              <li className="flex items-center">
                <span className="text-orange-500 mr-2">⚠️</span>7 days before expiration
              </li>
              <li className="flex items-center">
                <span className="text-red-500 mr-2">🚨</span>1 day before expiration
              </li>
            </ul>
          </div>
          <DocAlert variant="info">
            <p className={`text-sm ${docsColors.text.body}`}>
              <strong className={docsColors.text.heading}>Note:</strong> Certificate checks are
              performed at least once daily, independent of your monitor&apos;s check frequency.
              This is included with HTTP/HTTPS monitoring at no extra cost.
            </p>
          </DocAlert>
        </DocCard>
      </DocSection>

      {/* Additional Options */}
      <DocSection title="Additional Options">
        <DocCard>
          <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>Follow Redirects</h3>
          <p className={`${docsColors.text.body} mb-3`}>
            Enable to automatically follow HTTP redirects (3xx status codes). When enabled:
          </p>
          <ul className={`space-y-2 ${docsColors.text.body} ml-6`}>
            <li>• The monitor follows up to 5 redirects</li>
            <li>• Final destination status determines success</li>
            <li>• Response time includes all redirect hops</li>
            <li>• Useful for shortened URLs or load balancers</li>
          </ul>
          <p className={`text-sm ${docsColors.text.muted} mt-3`}>
            Disable this if you need to monitor the redirect response itself (e.g., to verify a
            redirect is working correctly).
          </p>
        </DocCard>
      </DocSection>

      {/* Common Use Cases */}
      <DocSection title="Common Use Cases">
        <div className="grid gap-4">
          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Website Monitoring</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Monitor your website&apos;s home page with GET request, follow redirects enabled, and
              response body validation for key content.
            </p>
          </DocCard>
          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>API Health Checks</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Use GET or POST to health endpoints, validate JSON responses, check specific status
              codes, and include authentication headers.
            </p>
          </DocCard>
          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>GraphQL Endpoints</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              POST requests with GraphQL queries in the body, Content-Type header, and response
              validation for expected data structure.
            </p>
          </DocCard>
          <DocCard className="p-4">
            <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>Protected Endpoints</h3>
            <p className={`${docsColors.text.body} text-sm`}>
              Monitor APIs that require authentication by including Bearer tokens, API keys, or
              other auth headers in your requests.
            </p>
          </DocCard>
        </div>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/monitors", text: "Monitor Types Overview" }}
        next={{ href: "/docs/monitors/tcp", text: "TCP Port Monitoring" }}
      />
    </DocLayout>
  );
}
